/**
 * Couple stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const coupleStickersPack: ToilePack = {
    slug: 'couple-stickers',
    category: 'sticker',
    name: 'Couple stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'hug', label: 'Hug', subject: 'Two round characters hugging with hearts above'},
        {key: 'drink', label: 'Sharing a drink', subject: 'Two round characters sharing one drink with two straws'},
        {key: 'kiss', label: 'Blowing a kiss', subject: 'A round character blowing a kiss with a heart floating away'},
    ],
    styles: [
        {key: 'chat', label: 'Chat sticker', style: 'messaging-app sticker, expressive cartoon, thick outline, bold flat colour', palette: 'Bright flat colour with a white outline', framing: 'The sticker isolated on a plain ground with a white outline, built to read at chat size'},
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['as best friends instead', 'in a single warm colour', 'for an anniversary card'],
    directionHint: 'try: as best friends instead',
    seo: {
        title: 'Couple Stickers: Hugging, Sharing a Drink, Kiss',
        metaDescription: 'Nine couple stickers — two characters hugging, two sharing a drink, one blowing a kiss — in chat, kawaii, and doodle styles.',
        h1: 'Couple stickers: hugging, sharing a drink, and a kiss',
        tagline: 'Nine couple stickers, three moments, three styles each.',
        intro: 'The sticker for two: two characters hugging, two sharing a drink, one blowing a kiss. Rendered chat, kawaii, and doodle so the set fits whatever tone your shared chat with a partner already has. Ask for a different moment — holding hands, dancing, a long-distance wave — and the same three styles carry it.',
        useCases: [
            "a couple's shared chat sticker pack",
            "an anniversary or Valentine's gift sticker",
            'a scrapbook or memory-book accent',
            'a long-distance relationship sticker set',
            'a printable couple sticker sheet',
        ],
        faq: [
            {q: 'Are the two characters generic, or based on a real photo?', a: 'These are original illustrated characters, not a likeness of any real people — no photo reference is used.'},
            {q: 'Can I get a different moment, like holding hands or dancing?', a: 'Yes — the direction field takes any shared moment, rendered in the same three styles.'},
            {q: "What's the difference between chat, kawaii, and doodle?", a: 'Chat is a rounded messaging-app character style, kawaii is softer and big-eyed, and doodle is a hand-drawn sketch look — pick whichever suits the gift or chat.'},
            {q: "Are these good for a Valentine's or anniversary gift?", a: "Yes — the couple's-moment framing is designed to work as a small, personal gift sticker sheet."},
            {q: "Can I use these in a scrapbook I'm making as a gift?", a: 'Yes, for personal and gift use; check the license terms before reselling the designs themselves.'},
        ],
    },
}
